import {
  MediaItem,
  MediaUploadArgs,
  getContentTypeByExt,
  getUploadBlobNameForMediaType,
  useMediaUpload as useMediaUploadBase,
} from '@gripp/shared-logic';
import { useContainerClient } from './useContainerClient';

export const useMediaUpload = (args: MediaUploadArgs) => {
  const containerClient = useContainerClient();

  const uploadToBlobStorage = async (mediaItem: MediaItem, file?: File) => {
    // Throw rather than return: the media record already exists as PENDING, so
    // a silent no-op leaves callers holding an id whose bytes never arrive.
    if (!containerClient || !file) {
      throw new Error(
        `Cannot upload media item ${mediaItem.id}: no ${
          containerClient ? 'file' : 'storage container client'
        }.`
      );
    }

    const onUploadProgress = (progress: any, mediaSize: number) => {
      if (!args.onProgress) return;

      const percent = progress.loadedBytes / mediaSize;
      args.onProgress(mediaItem.id, percent);
    };

    const blobName = getUploadBlobNameForMediaType(
      mediaItem.name,
      mediaItem.id,
      mediaItem.type,
      mediaItem.extension
    );
    const blobContentType = getContentTypeByExt(mediaItem.extension);
    const blockBlobClient = containerClient.getBlockBlobClient(blobName);
    const fileMediaUrl = (await createWebUrlForLocalFile(file)) as string;
    mediaItem.url = fileMediaUrl;

    args.onStart && args.onStart(mediaItem);

    // Failures propagate to useMediaUploadBase, which logs them and calls
    // onError; onComplete must only mean the bytes actually landed.
    const response = await blockBlobClient.uploadData(file, {
      blobHTTPHeaders: {
        blobContentType: blobContentType,
      },

      onProgress: (progressEvent) => onUploadProgress(progressEvent, file.size),
    });

    if (response.errorCode) {
      throw new Error(
        `Failed to upload media item: ${mediaItem.id}. Error: ${response.errorCode}`
      );
    }

    args.onComplete && args.onComplete(mediaItem.id);
  };

  const uploadMediaBase = useMediaUploadBase({
    args: args,
    upload: uploadToBlobStorage,
  });

  const uploadMedia = async (
    file: File,
    workspaceId?: string,
    name?: string,
    categories?: Array<{ id: string }>,
    overrideExtension?: string
  ) => {
    return uploadMediaBase(file?.name, {
      workspaceId,
      file,
      name,
      categories,
      overrideExtension,
    });
  };

  return uploadMedia;
};

const createWebUrlForLocalFile = (file: File) => {
  return new Promise((resolve, reject) => {
    try {
      const url = URL.createObjectURL(file);
      resolve(url);
    } catch (error) {
      reject(error);
    }
  });
};
